import { useEffect, useState } from "react"
import { Button, Modal, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { APIService } from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"

interface Recipient {
  id: string
  firstName: string
  lastName: string
  email: string
  isConfigured: boolean
  notifyOnNew: boolean
  notifyOnStatusChange: boolean
  notifyOnComment: boolean
  notifyOnDigest: boolean
}

interface Props {
  show: boolean
  toggle: (section: string) => void
}

const IncidentNotificationsSettings = ({ show, toggle }: Props) => {
  const { t } = useTranslation("global")
  const user: any = parsedUser()

  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!show) return
    load()
  }, [show])

  const load = async () => {
    setIsLoading(true)
    try {
      const res = await APIService.getAction(
        "Incident/NotificationRecipients/List",
        `organizationId=${user.OrganizationId}`
      )
      if (res.data.status === "ok") setRecipients(res.data.response ?? [])
    } finally {
      setIsLoading(false)
    }
  }

  const toggle_field = (id: string, field: keyof Recipient) => {
    setRecipients(prev =>
      prev.map(r => r.id === id ? { ...r, [field]: !r[field] } : r)
    )
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const payload = {
        organizationId: user.OrganizationId,
        updatedId: user.ID,
        recipients: recipients
          .filter(r => r.notifyOnNew || r.notifyOnStatusChange || r.notifyOnComment || r.notifyOnDigest)
          .map(r => ({
            userId: r.id,
            notifyOnNew: r.notifyOnNew,
            notifyOnStatusChange: r.notifyOnStatusChange,
            notifyOnComment: r.notifyOnComment,
            notifyOnDigest: r.notifyOnDigest,
          })),
      }
      const res = await APIService.postAction("Incident/NotificationRecipients/Save", payload)
      if (res.data.status === "ok") {
        toast.success(t("record saved"))
        toggle("")
      }
    } finally {
      setIsSaving(false)
    }
  }

  if (!show) return null

  return (
    <Modal show={show} onHide={() => toggle("")} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="h5 fw-bold mb-1">
            <i className="fas fa-bell text-warning me-2" />
            {t("Notificaciones de Incidencias")}
          </Modal.Title>
          <small className="text-muted">
            {t("Configura quién recibe alertas cuando se crean o actualizan incidencias. Si no hay nadie configurado, se notifica a todos los admins.")}
          </small>
        </div>
      </Modal.Header>
      <Modal.Body className="p-0">
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" className="me-2 text-primary" />
            <span className="small text-muted">{t("Cargando destinatarios...")}</span>
          </div>
        ) : recipients.length === 0 ? (
          <div className="text-center py-5 text-muted small">
            {t("No se encontraron usuarios en la organización.")}
          </div>
        ) : (
          <Table hover responsive className="mb-0 small align-middle">
            <thead className="table-light">
              <tr>
                <th className="ps-3">{t("USUARIO")}</th>
                <th className="text-center">{t("NUEVAS INCIDENCIAS")}</th>
                <th className="text-center">{t("CAMBIOS DE ESTADO")}</th>
                <th className="text-center">{t("NUEVOS COMENTARIOS")}</th>
                <th className="text-center pe-3">{t("DIGEST SEMANAL (VIERNES)")}</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map(r => (
                <tr key={r.id}>
                  <td className="ps-3">
                    <div className="fw-semibold text-dark">{r.firstName} {r.lastName}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>{r.email}</div>
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={r.notifyOnNew}
                      onChange={() => toggle_field(r.id, "notifyOnNew")}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={r.notifyOnStatusChange}
                      onChange={() => toggle_field(r.id, "notifyOnStatusChange")}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={r.notifyOnComment}
                      onChange={() => toggle_field(r.id, "notifyOnComment")}
                    />
                  </td>
                  <td className="text-center pe-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={r.notifyOnDigest}
                      onChange={() => toggle_field(r.id, "notifyOnDigest")}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button variant="outline-secondary" size="sm" onClick={() => toggle("")}>
          {t("Cancelar")}
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving || isLoading}>
          {isSaving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {t("Guardando...")}
            </>
          ) : (
            <>
              <i className="fas fa-save me-2" />
              {t("Guardar configuración")}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default IncidentNotificationsSettings
